import { lazy, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../lib/auth';
import { PublicLayout } from '../layouts/PublicLayout';

/** The very page the catch-all route renders — see App.tsx. */
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

/**
 * Route guards (CONTRACT.md §2.5, storefront-redesign-2026-q4.md §8 Fase 0).
 *
 * RequireAuth and RequireAdmin answer different questions on purpose.
 * RequireAuth protects a customer's own data — being asked to log in first is
 * an honest response, so it redirects to /login and remembers where to
 * return. RequireAdmin protects a boundary an outsider should not even learn
 * exists — confirming "you're not allowed" (via a login redirect) still
 * discloses that /admin is a real, gated route. Rendering the same NotFoundPage
 * inside the same PublicLayout as an unmapped path keeps the answer for "wrong
 * role" and "no such route" indistinguishable from outside.
 */

/**
 * safeRedirectPath guards the `lanjut` query param against open redirect.
 *
 * A value is only accepted when it is unambiguously an internal path: it must
 * start with a single '/'. '//evil.example' and '/\evil.example' are both
 * browser-legal ways to spell a scheme-relative URL that navigates off-site
 * (browsers treat backslash as a path separator the same as forward slash),
 * so both are rejected alongside absolute URLs like 'https://evil.example'.
 * Anything that fails the check falls back to '/', never to the untrusted
 * value itself.
 */
export function safeRedirectPath(value: string | null): string {
  if (!value) return '/';
  if (!value.startsWith('/')) return '/';
  if (value.startsWith('//') || value.startsWith('/\\')) return '/';
  return value;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  if (!authService.isAuthenticated()) {
    const lanjut = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?lanjut=${lanjut}`} replace />;
  }
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const user = authService.isAuthenticated() ? authService.getUser() : null;
  if (user?.role !== 'admin') {
    // The same component tree the `*` route renders, layout included. A 404 that
    // looks different from the catch-all is itself the confirmation that /admin
    // exists — see guards.test.tsx, which compares the two trees.
    return (
      <PublicLayout>
        <NotFoundPage />
      </PublicLayout>
    );
  }
  return <>{children}</>;
}
